/**
 * Declarative config of the claim « Détails » tab (`extranet.claimDetailsCard`,
 * fiche 22807): the `EntityInfoCard` fields (stable keys, French labels) and
 * the values builder from the `WS2_EA_FIC_SIN` record.
 *
 * Stable keys: `reference`, `statut`, `produit`, `dateSurvenance`,
 * `dateDeclaration`, `type`, `coutAssureur`, then the long texts rendered
 * full width — `circonstances`, `description`, `dommages`.
 */

import type { ClaimRecord } from '../../api/claimsService';
import { formatEur, formatVeosDate } from '../../utils/format';
import type { EntityInfoField, EntityInfoValue } from '../shared/EntityInfoCard';

/** Stable keys of the claim details card. */
export type ClaimDetailsFieldKey =
  | 'reference'
  | 'statut'
  | 'produit'
  | 'dateSurvenance'
  | 'dateDeclaration'
  | 'type'
  | 'coutAssureur'
  | 'circonstances'
  | 'description'
  | 'dommages';

/** Base fields, in mockup order (short fields in the grid, long texts below). */
export const CLAIM_DETAILS_FIELDS: readonly (EntityInfoField & { key: ClaimDetailsFieldKey })[] = [
  { key: 'reference', label: 'Référence' },
  { key: 'statut', label: 'Statut' },
  { key: 'produit', label: 'Produit' },
  { key: 'dateSurvenance', label: 'Date de survenance' },
  { key: 'dateDeclaration', label: 'Date de déclaration' },
  { key: 'type', label: 'Type' },
  { key: 'coutAssureur', label: 'Coût assureur', tooltip: "Coût total pour l'assureur" },
  { key: 'circonstances', label: 'Circonstances', wide: true },
  {
    key: 'description',
    label: 'Description',
    tooltip: 'Cause et déroulement du sinistre',
    wide: true,
  },
  { key: 'dommages', label: 'Dommages', tooltip: 'Description des dommages', wide: true },
];

/** `''` ⇒ `null`, so an empty formatted value keeps its slot without text. */
function orNull(text: string): string | null {
  return text === '' ? null : text;
}

/**
 * Values of the details card keyed by field key — dates `JJ/MM/AAAA` (Paris
 * calendar day), cost in euros with 2 decimals, `TYPE_SIN` verbatim, `null`
 * for a missing value. `null` record (VEOS returned none) ⇒ `null`
 * (« aucune donnée disponible »).
 */
export function buildClaimDetailsValues(
  record: ClaimRecord | null,
): Record<ClaimDetailsFieldKey, EntityInfoValue> | null {
  if (record === null) return null;
  return {
    reference: record.reference,
    statut: record.libelleStatut,
    produit: record.produit,
    dateSurvenance: orNull(formatVeosDate(record.dateSurvenance)),
    dateDeclaration: orNull(formatVeosDate(record.dateDeclaration)),
    type: record.type,
    coutAssureur: orNull(formatEur(record.coutAssureur)),
    circonstances: record.circonstances,
    description: record.description,
    dommages: record.dommages,
  };
}
